import React, { useEffect, useMemo, useState } from 'react';
import {
  Heading,
  Loader,
  Flex,
  Text,
  Avatar,
  Label,
} from '@vibe/core';
import {
  getUserSettings,
  type UserSettings,
} from '../../api/settings.api';
import { getMyTimesheetApprover, type ApproverEntry } from '../../api/permission.api';
import {
  getMyCapacityScheme,
  type MyCapacitySchemeResponse,
  type DaySchedule,
  DAY_KEYS,
} from '../../api/capacity.api';
import { fetchMondayUsers, type MondayUser } from '../../api/monday.api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

const DAY_LABELS: Record<keyof DaySchedule, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

function formatScheduleSummary(schedule: DaySchedule): string {
  const totalHours = DAY_KEYS.reduce((sum, day) => sum + schedule[day], 0);
  const workDays = DAY_KEYS.filter((day) => schedule[day] > 0);
  return `${totalHours}h/week · ${workDays.length} days`;
}

// ─── Approver Card ────────────────────────────────────────────────────────────

// ─── Approver Row (single entry) ─────────────────────────────────────────────

const ApproverRow: React.FC<{
  entry: ApproverEntry;
  userMap: Map<string, MondayUser>;
  teamMap: Map<string, string>;
}> = ({ entry, userMap, teamMap }) => {
  const isTeam = entry.granteeType === 'team';
  const user = !isTeam ? userMap.get(entry.granteeId) : undefined;
  const teamName = isTeam ? teamMap.get(entry.granteeId) : undefined;

  if (isTeam) {
    return (
      <div className="rp-my-settings__approver-row">
        <Avatar
          size={Avatar.sizes.MEDIUM}
          text={getInitials(teamName ?? 'Team')}
          ariaLabel={teamName ?? `Team ${entry.granteeId}`}
          type={Avatar.types.TEXT}
          backgroundColor={Avatar.backgroundColors.DARK_PURPLE}
        />
        <div className="rp-my-settings__approver-info">
          <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
            {teamName ?? `Team ${entry.granteeId}`}
          </Text>
          <Text
            type={Text.types.TEXT2}
            style={{ color: 'var(--secondary-text-color, #676879)', fontSize: 12 }}
          >
            Team
          </Text>
        </div>
      </div>
    );
  }

  if (user) {
    return (
      <div className="rp-my-settings__approver-row">
        <Avatar
          size={Avatar.sizes.MEDIUM}
          src={user.photo_thumb_small ?? undefined}
          text={getInitials(user.name)}
          ariaLabel={user.name}
          type={Avatar.types.IMG}
        />
        <div className="rp-my-settings__approver-info">
          <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
            {user.name}
          </Text>
          {user.email && (
            <Text
              type={Text.types.TEXT2}
              style={{ color: 'var(--secondary-text-color, #676879)', fontSize: 12 }}
            >
              {user.email}
            </Text>
          )}
          {user.title && (
            <Text
              type={Text.types.TEXT2}
              style={{ color: 'var(--secondary-text-color, #9699a6)', fontSize: 11 }}
            >
              {user.title}
            </Text>
          )}
        </div>
      </div>
    );
  }

  return (
    <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
      User {entry.granteeId}
    </Text>
  );
};

// ─── Approver Card ───────────────────────────────────────────────────────────

interface ApproverCardProps {
  label: string;
  description: string;
  approvers: ApproverEntry[];
  userMap: Map<string, MondayUser>;
  teamMap: Map<string, string>;
}

const ApproverCard: React.FC<ApproverCardProps> = ({
  label,
  description,
  approvers,
  userMap,
  teamMap,
}) => {
  return (
    <div className="rp-my-settings__field">
      <label className="rp-my-settings__label">{label}</label>
      <Text
        type={Text.types.TEXT2}
        style={{ color: 'var(--secondary-text-color, #676879)', marginBottom: 8 }}
      >
        {description}
      </Text>
      {approvers.length > 0 ? (
        <div className="rp-my-settings__approver-list">
          {approvers.map((entry) => (
            <div
              key={`${entry.granteeType}-${entry.granteeId}`}
              className="rp-my-settings__read-only-value"
            >
              <ApproverRow
                entry={entry}
                userMap={userMap}
                teamMap={teamMap}
              />
            </div>
          ))}
        </div>
      ) : (
        <Text type={Text.types.TEXT2} style={{ color: 'var(--negative-color, #e2445c)' }}>
          No {label.toLowerCase()} has been assigned to you. Please contact your
          admin.
        </Text>
      )}
    </div>
  );
};

// ─── Capacity Scheme Card ─────────────────────────────────────────────────────

interface CapacitySchemeCardProps {
  schemeData: MyCapacitySchemeResponse;
}

const CapacitySchemeCard: React.FC<CapacitySchemeCardProps> = ({
  schemeData,
}) => {
  const { scheme, source } = schemeData;

  return (
    <div className="rp-my-settings__field">
      <label className="rp-my-settings__label">Work Capacity Scheme</label>
      <Text
        type={Text.types.TEXT2}
        style={{ color: 'var(--secondary-text-color, #676879)', marginBottom: 8 }}
      >
        Your work capacity defines the expected working hours per day. It is
        used for workload calculations and timesheet validation.
      </Text>
      {scheme ? (
        <div className="rp-my-settings__read-only-value">
          <Flex
            direction={Flex.directions.ROW}
            align={Flex.align.CENTER}
            gap={Flex.gaps.SMALL}
            style={{ marginBottom: 8 }}
          >
            <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
              {scheme.name}
            </Text>
            {source === 'default' && (
              <Label
                text="Default"
                kind={Label.kinds.LINE}
                color={Label.colors.PRIMARY}
              />
            )}
          </Flex>
          <Text
            type={Text.types.TEXT2}
            style={{ color: 'var(--secondary-text-color, #676879)', fontSize: 12, marginBottom: 8 }}
          >
            {formatScheduleSummary(scheme.schedule)}
          </Text>
          <div className="rp-my-settings__schedule-grid">
            {DAY_KEYS.map((day) => (
              <div
                key={day}
                className={`rp-my-settings__schedule-day${
                  scheme.schedule[day] === 0
                    ? ' rp-my-settings__schedule-day--off'
                    : ''
                }`}
              >
                <span className="rp-my-settings__schedule-day-label">
                  {DAY_LABELS[day]}
                </span>
                <span className="rp-my-settings__schedule-day-hours">
                  {scheme.schedule[day]}h
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <Text type={Text.types.TEXT2} style={{ color: 'var(--negative-color, #e2445c)' }}>
          No capacity scheme has been configured. Please contact your admin.
        </Text>
      )}
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Per-user settings panel.
 *
 * Displays read-only information about the user's admin-assigned approvers
 * and capacity scheme.
 */
const MySettings: React.FC = () => {
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [timesheetApprovers, setTimesheetApprovers] = useState<ApproverEntry[]>(
    [],
  );
  const [capacityScheme, setCapacityScheme] =
    useState<MyCapacitySchemeResponse | null>(null);
  const [users, setUsers] = useState<MondayUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Build a string-keyed map for easy lookup (backend IDs are strings)
  const userMap = useMemo(() => {
    const map = new Map<string, MondayUser>();
    for (const u of users) {
      map.set(String(u.id), u);
    }
    return map;
  }, [users]);

  // Build a team name map from users' team data
  const teamMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) {
      for (const t of u.teams) {
        map.set(String(t.id), t.name);
      }
    }
    return map;
  }, [users]);

  // ─── Load settings + users ──────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [data, approverData, schemeData, mondayUsers] = await Promise.all(
          [
            getUserSettings(),
            getMyTimesheetApprover(),
            getMyCapacityScheme(),
            fetchMondayUsers(),
          ],
        );
        if (cancelled) return;

        setSettings(data);
        setTimesheetApprovers(approverData.approvers);
        setCapacityScheme(schemeData);
        setUsers(mondayUsers);
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error
              ? err.message
              : 'Failed to load user settings';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="rp-my-account">
        <div className="rp-my-account__loading">
          <Loader size={Loader.sizes.MEDIUM} />
        </div>
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="rp-my-account">
        <div className="rp-my-account__error">
          <Text style={{ color: 'var(--negative-color, #e2445c)' }}>{error}</Text>
        </div>
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div className="rp-my-account">
      <div className="rp-my-account__header">
        <Heading type={Heading.types.H2}>My Account</Heading>
        <Text
          type={Text.types.TEXT2}
          style={{ color: 'var(--secondary-text-color, #676879)', marginTop: 4 }}
        >
          Personal settings that apply only to your account.
        </Text>
      </div>

      <div className="rp-my-account__cards">
        <ApproverCard
          label="Leave Approver"
          description="Your leave approver is assigned by an admin. All leave requests will be routed to this person for approval."
          approvers={
            settings.defaultLeaveApprover
              ? [{ granteeId: settings.defaultLeaveApprover, granteeType: 'user' }]
              : []
          }
          userMap={userMap}
          teamMap={teamMap}
        />

        <ApproverCard
          label="Timesheet Approver"
          description="Your timesheet approver is assigned by an admin. Submitted timesheets will be routed to this person for review."
          approvers={timesheetApprovers}
          userMap={userMap}
          teamMap={teamMap}
        />

        {capacityScheme && <CapacitySchemeCard schemeData={capacityScheme} />}
      </div>
    </div>
  );
};

export default MySettings;
